import React, { useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { Button, Col, Row } from "reactstrap";
import {
  Button as AntdButton,
  Card,
  Table,
  Space,
  message,
  Popover,
  Modal,
  Radio,
  Checkbox,
  Tooltip,
} from "antd";
import { EditOutlined, InfoCircleOutlined, SettingOutlined } from "@ant-design/icons";
import FormInput from "../../form-components/InputComponent";
import FormSelect from "../../form-components/SelectComponent";
import FormTextArea from "../../form-components/TextAreaComponent";
import FormDatePicker from "../../form-components/DatePicker";
import {
  WorkOrderFormData,
  ContractType,
  ContractWith,
  TaxApplicationLevel,
} from "../types";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import usersApis from "../../../api/master/users";
import misc from "../../../api/misc/dropDowns";
import WorkOrderApprovalDrawer from "./WorkOrderApprovalDrawer";
import { Form } from "react-router-dom";
import dropdownApis from "../../../api/WorkOrderManagement/DropdownApis";
import { parse } from "path";
import sequenceApis from "../../../api/master/sequence";

interface SelectOptions {
  label: string;
  value: string;
}

const WorkOrderGeneralDetails: React.FC = (props: any) => {
  const {
    handleSubmit,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useFormContext<any>();
  const location = useLocation();

  // Get flowType from URL
  const searchParams = new URLSearchParams(location.search);
  const flowType = searchParams.get("flowType");
  const isClientFlow = flowType?.toLowerCase() === "client";

  const [sitesOptions, setSitesOptions] = useState<SelectOptions[]>([]);
  const [projectOptions, setProjectOptions] = useState<SelectOptions[]>([]);
  const [contractorOptions, setContractorOptions] = useState<SelectOptions[]>(
    []
  );
  const [workRequestOptions, setWorkRequestOptions] = useState<SelectOptions[]>(
    []
  );
  const [tenderOptions, setTenderOptions] = useState<SelectOptions[]>([]);
  const [userOptions, setUserOptions] = useState<SelectOptions[]>([]);
  const [taxOptions, setTaxOptions] = useState<SelectOptions[]>([]);
  const [tdsTaxOptions, setTdsTaxOptions] = useState<SelectOptions[]>([]);
  const [tcsTaxOptions, setTcsTaxOptions] = useState<SelectOptions[]>([]);
  const [budgetOptions, setBudgetOptions] = useState<SelectOptions[]>([]);
  const [approvals, setApprovals] = useState({
    open: false,
    data: [],
  });
  const [isStrictApproval, setIsStrictApproval] = useState<boolean>(false);
  const [isFormDataFetched, setIsFormDataFetched] = useState(false);

  // State management for GST calculation
  const [contractorState, setContractorState] = useState<string>("");
  const [currentUserState, setCurrentUserState] = useState<string>("");
  const [contractorsStateMap, setContractorsStateMap] = useState<{
    [key: string]: string;
  }>({});

  // Get current user from Redux store
  const currentUser = useSelector((state: any) => state.currentUser?.data);

  // Watch form values
  const siteIdWatch = watch("siteId");
  const contractWithWatch = watch("contractWith");
  const contractorIdWatch = watch("contractorId");
  const approvalRequiredWatch = watch("approvalRequired");
  const enableTaxWatch = watch("enableTax");
  const taxApplicationLevelWatch = watch("taxApplicationLevel");
  const contractDateWatch = watch("contractDate");
  const plannedStartDateWatch = watch("plannedStartDate");
  const AllFormDetails = watch();

  // Contract Type options as per FSD
  const contractTypeOptions: SelectOptions[] = [
    { label: "Construction Work", value: "Construction" },
    { label: "Service", value: "Service" },
    { label: "Maintenance", value: "Maintenance" },
    { label: "Others", value: "Others" },
  ];

  // Contract With options as per FSD
  const contractWithOptions: SelectOptions[] = [
    { label: "General Contractor", value: "GC" },
    { label: "Sub Contractor", value: "SC" },
    { label: "Others", value: "Others" },
  ];

  // Tax Application Level options
  const taxApplicationLevelOptions: SelectOptions[] = [
    { label: "Header Level", value: TaxApplicationLevel.Header },
    { label: "Line Level", value: TaxApplicationLevel.Line },
    { label: "Both", value: TaxApplicationLevel.Both },
  ];

  useEffect(() => {
    fetchMasterData();
    fetchTaxOptions();
    fetchTdsTaxOptions();
    fetchTcsTaxOptions();
  }, []);

  // Set current user state when component mounts
  useEffect(() => {
    if (currentUser?.registeredAddress?.state?.stateId) {
      setCurrentUserState(
        currentUser.registeredAddress.state.stateId.toString()
      );
      console.log(
        "Current user state ID:",
        currentUser.registeredAddress.state.stateId
      );
    }
  }, [currentUser]);

  // Set contractor state when contractor is selected
  useEffect(() => {
    if (
      contractorIdWatch &&
      contractorIdWatch !== "" &&
      contractorsStateMap[contractorIdWatch]
    ) {
      const stateId = contractorsStateMap[contractorIdWatch];
      setContractorState(stateId);
      console.log(
        "Contractor selected:",
        contractorIdWatch,
        "State ID:",
        stateId
      );
      console.log("Current user state:", currentUserState);
      console.log("States match:", stateId === currentUserState);
    }
  }, [contractorIdWatch, contractorsStateMap, currentUserState]);

  useEffect(() => {
    console.log("=== SITE CHANGE EFFECT (SIMPLIFIED) ===");
    console.log("siteIdWatch:", siteIdWatch);

    if (siteIdWatch && siteIdWatch !== "") {
      console.log("Fetching dependent data for siteId:", siteIdWatch);
      fetchDependentData(siteIdWatch);
      fetchBudgetOptions(siteIdWatch);
      fetchSegmentLevel(siteIdWatch);
    } else {
      console.log("No siteId - clearing dropdown options");
      // Clear dependent data when no site is selected
      setProjectOptions([]);
      setContractorOptions([]);
      setUserOptions([]);
      setWorkRequestOptions([]);
      setTenderOptions([]);
      setBudgetOptions([]);
      setValue("workLevel", "");
    }
  }, [siteIdWatch]); // ← Simplified to match working BOQ pattern

  // Fetch Segment Level (Work Level Settings) based on site selection
  const fetchSegmentLevel = async (siteId: string) => {
    try {
      const { data, status } = await dropdownApis.fetchSegmentLevelBySiteId(
        parseInt(siteId)
      );
      if (status && data) {
        // Set workLevel from API response, default to "Site" if empty
        if (data === "Tower") {
          setValue("workLevel", "Tower / Block");
        } else {
          setValue("workLevel", data || "Site");
        }
        console.log("Work Level set from site config:", data);
      } else {
        setValue("workLevel", "Site");
      }
    } catch (error) {
      console.log("Error fetching segment level:", error);
      setValue("workLevel", "Site");
    }
  };

  // Simple restoration effect - runs once after all options are loaded
  useEffect(() => {
    console.log("=== SIMPLE RESTORATION CHECK ===");

    // Only restore if we have all options loaded and form has values
    if (
      contractorOptions.length > 0 &&
      userOptions.length > 0 &&
      budgetOptions.length > 0
    ) {
      console.log("All options loaded, checking for restoration needs...");

      const formValues = watch();
      console.log("Current form values:", {
        contractorId: formValues.contractorId,
        workOrderOwner: formValues.workOrderOwner,
        budgetId: formValues.budgetId,
        projectId: formValues.projectId,
      });

      // Restore contractor name if missing
      if (formValues.contractorId && !formValues.contractorName) {
        const contractor = contractorOptions.find(
          (c) => c.value === formValues.contractorId
        );
        if (contractor) {
          console.log("Restoring contractor name:", contractor.label);
          setValue("contractorName", contractor.label);
        }
      }

      // Restore work order owner name if missing
      if (formValues.workOrderOwner && !formValues.workOrderOwnerName) {
        const user = userOptions.find(
          (u) => u.value === formValues.workOrderOwner
        );
        if (user) {
          console.log("Restoring user name:", user.label);
          setValue("workOrderOwnerName", user.label);
        }
      }

      // Restore project name if missing
      if (
        formValues.projectId &&
        !formValues.projectName &&
        projectOptions.length > 0
      ) {
        const project = projectOptions.find(
          (p) => p.value === formValues.projectId
        );
        if (project) {
          console.log("Restoring project name:", project.label);
          setValue("projectName", project.label);
        }
      }
    }
  }, [
    contractorOptions,
    userOptions,
    budgetOptions,
    projectOptions,
    watch,
    setValue,
  ]);

  // const fetchMasterData = async () => {
  //   try {
  //     const masterData = getMasterData();

  //     // Set sites options
  //     const sitesOptions = masterData.sites.map((site) => ({
  //       label: site.name,
  //       value: site.id,
  //     }));
  //     setSitesOptions(sitesOptions);

  //     // Set users options for work order owner
  //     const usersOptions = masterData.users.map((user) => ({
  //       label: user.name,
  //       value: user.id,
  //     }));
  //     setUserOptions(usersOptions);
  //   } catch (error) {
  //     console.log("Error fetching master data:", error);
  //     message.error("Failed to load master data");
  //   }
  // };
  const fetchMasterData = async () => {
    try {
      const data = await usersApis.getCurrentUser();
      if (data) {
        const sitesOptions = data?.siteAccessibility?.map((item: any) => ({
          label: item.siteName,
          value: item.siteID.toString(),
        }));
        setSitesOptions(sitesOptions);
      }
    } catch (error) {
      console.log("Error fetching sites:", error);
      message.error("Failed to load sites data");
    } finally {
      // setLoading((prev) => ({ ...prev, sites: false }));
    }
  };

  const fetchUsersData = async (siteId: string) => {
    try {
      console.log("Fetching users for site:", siteId);
      const response = await misc.fetchSiteUsers(siteId);
      console.log("Users API response:", response);

      if (response?.status && response?.data) {
        const userOptions = response.data.map((user: any) => ({
          label: user.name || user.userName || user.fullName,
          value: user.appUserId?.toString(),
        }));
        setUserOptions(userOptions);
        console.log("User options set:", userOptions.length, "options");
      } else {
        setUserOptions([]);
        console.log("No users found for site");
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      message.error("Failed to load users data");
      setUserOptions([]);
    }
  };

  const fetchContractorsData = async (siteId: string) => {
    try {
      console.log("Fetching contractors for site:", siteId);

      // Get flowType from URL to determine partyType
      const currentSearchParams = new URLSearchParams(location.search);
      const currentFlowType = currentSearchParams.get("flowType");
      const isClient = currentFlowType?.toLowerCase() === "client";

      // Determine partyType based on flowType
      const partyType = isClient ? "Client" : "Contractor";

      const response = await dropdownApis.fetchContractorsBySiteIdDropdown({
        id: siteId.toString(),
        partyType: partyType,
      });
      console.log("Contractors API response:", response);

      if (response?.status && response?.data) {
        const contractorOptions = response.data.map((contractor: any) => ({
          label: contractor.companyName || contractor.contractorName,
          value: (
            contractor.contractorID ||
            contractor.contractorId ||
            contractor.id
          )?.toString(),
        }));

        // Create state mapping for GST calculation
        const stateMap: { [key: string]: string } = {};
        response.data.forEach((contractor: any) => {
          const contractorId = (
            contractor.contractorID ||
            contractor.contractorId ||
            contractor.id
          )?.toString();

          const stateId = contractor.state;

          if (contractorId && stateId) {
            stateMap[contractorId] = stateId.toString();
          }
        });

        setContractorOptions(contractorOptions);
        setContractorsStateMap(stateMap);
        console.log(
          "Contractor options set:",
          contractorOptions.length,
          "options"
        );
      } else {
        setContractorOptions([]);
        setContractorsStateMap({});
        console.log("No contractors found for site");
      }
    } catch (error) {
      console.error("Error fetching contractors:", error);
      message.error(
        isClientFlow ? "Failed to load clients data" : "Failed to load contractors data"
      );
      setContractorOptions([]);
      setContractorsStateMap({});
    }
  };

  const fetchDependentData = async (siteId: string) => {
    try {
      console.log("Fetching dependent data for site:", siteId);

      // Fetch projects for the selected site
      const { data: projects, status } = await dropdownApis.getProjectsDropdown(
        parseInt(siteId)
      );
      if (status && projects) {
        const projectOptions = projects.map((project: any) => ({
          label: project.projectTitle,
          value: project.projectId?.toString(),
        }));
        setProjectOptions(projectOptions);
        console.log("Project options set:", projectOptions.length, "options");
      } else {
        setProjectOptions([]);
      }

      // Fetch contractors for the selected site
      await fetchContractorsData(siteId);

      // Fetch users for the selected site
      await fetchUsersData(siteId);

      // Update site name in form - get from current site options
      const selectedSiteOption = sitesOptions.find(
        (site) => site.value === siteId
      );
      if (selectedSiteOption) {
        setValue("siteName", selectedSiteOption.label);
      }
    } catch (error) {
      console.error("Error fetching dependent data:", error);
      message.error("Failed to load dependent data");
    }
  };

  const fetchTaxOptions = async () => {
    try {
      const response = await dropdownApis.fetchTaxDropdown();
      if (response?.data && Array.isArray(response.data)) {
        const taxOpts = response.data.map((tax: any) => ({
          label: `${tax.taxName || tax.name} - ${tax.taxRate || tax.rate || 0
            }%`,
          value: tax.taxId?.toString() || tax.id?.toString(),
        }));
        setTaxOptions(taxOpts);
        console.log("Tax options loaded:", taxOpts);
      }
    } catch (error) {
      console.error("Error loading tax options:", error);
      message.error("Failed to load tax options");
    }
  };

  const fetchTdsTaxOptions = async () => {
    try {
      const response = await dropdownApis.fetchTaxDropdown("TDS");
      if (response?.data && Array.isArray(response.data)) {
        const tdsOpts = response.data.map((tax: any) => ({
          label: `${tax.taxName || tax.name} - ${tax.taxRate || tax.rate || 0
            }%`,
          value: tax.taxId?.toString() || tax.id?.toString(),
        }));
        setTdsTaxOptions(tdsOpts);
        console.log("TDS tax options loaded:", tdsOpts);
      }
    } catch (error) {
      console.error("Error loading TDS tax options:", error);
      message.error("Failed to load TDS tax options");
    }
  };

  const fetchTcsTaxOptions = async () => {
    try {
      const response = await dropdownApis.fetchTaxDropdown("TCS");
      if (response?.data && Array.isArray(response.data)) {
        const tcsOpts = response.data.map((tax: any) => ({
          label: `${tax.taxName || tax.name} - ${tax.taxRate || tax.rate || 0
            }%`,
          value: tax.taxId?.toString() || tax.id?.toString(),
        }));
        setTcsTaxOptions(tcsOpts);
        console.log("TCS tax options loaded:", tcsOpts);
      }
    } catch (error) {
      console.error("Error loading TCS tax options:", error);
      message.error("Failed to load TCS tax options");
    }
  };

  const fetchBudgetOptions = async (siteId: string) => {
    try {
      console.log("Fetching budget options for site:", siteId);
      const response = await dropdownApis.getBudgetDropdownBySiteId(
        parseInt(siteId)
      );
      console.log("Budget API response:", response);

      if (response?.status && response?.data) {
        const budgetOpts = response.data.map((budget: any) => ({
          label: budget.budgetName,
          value: budget.headerId?.toString(),
        }));
        setBudgetOptions(budgetOpts);
        console.log("Budget options set:", budgetOpts.length, "options");
      } else {
        setBudgetOptions([]);
        console.log("No budget options found for site");
      }
    } catch (error) {
      console.error("Error fetching budget options:", error);
      message.error("Failed to load budget options");
      setBudgetOptions([]);
    }
  };

  const handleContractorChange = (contractorId: string) => {
    if (contractorId) {
      const selectedContractor = contractorOptions.find(
        (contractor) => contractor.value === contractorId
      );
      if (selectedContractor) {
        setValue("contractorName", selectedContractor.label);
      }
    } else {
      setValue("contractorName", "");
    }
  };

  const setFormValues = () => {
    const { approvalsData } = AllFormDetails;

    if (approvalsData) {
      const _approvals = JSON.parse(approvalsData);
      setApprovals(_approvals);
    }
  };

  useEffect(() => {
    if (!isFormDataFetched && Object.keys(AllFormDetails).length > 0) {
      setFormValues();
      setIsFormDataFetched(true);
    }
  }, [AllFormDetails]);

  const validateForm = (data: any) => {
    console.log("Validating data:", data);

    // Work Order Title validation
    if (!data.workOrderTitle?.trim()) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Work Order Title is required",
      });
      return false;
    }

    if (data.workOrderTitle.length > 200) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Work Order Title cannot exceed 200 characters",
      });
      return false;
    }

    // Contract Date validation
    if (!data.contractDate) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Contract Date is required",
      });
      return false;
    }

    // Site Selection validation
    if (!data.siteId) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Site Selection is required",
      });
      return false;
    }

    // Contract Type validation
    if (!data.contractType) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Contract Type is required",
      });
      return false;
    }

    // Contract With validation
    if (!data.contractWith) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Contract With is required",
      });
      return false;
    }

    // Contractor Selection validation (mandatory if contract is with General Contractor)
    if (
      data.contractWith === ContractWith.GeneralContractor &&
      !data.contractorId
    ) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Contractor Selection is required for General Contractor",
      });
      return false;
    }
    // Work Order Owner validation
    if (!data.workOrderOwner) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Work Order Owner is required",
      });
      return false;
    }

    // Planned Start Date validation
    if (!data.plannedStartDate) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Planned Start Date is required",
      });
      return false;
    }

    // Planned End Date validation
    if (!data.plannedEndDate) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Planned End Date is required",
      });
      return false;
    }

    // Date range validation
    if (data.plannedStartDate && data.plannedEndDate) {
      if (dayjs(data.plannedEndDate).isBefore(dayjs(data.plannedStartDate))) {
        Swal.fire({
          icon: "error",
          title: "Validation Error",
          text: "Planned End Date must be after Planned Start Date",
        });
        return false;
      }
    }

    // Approval Required validation
    if (!data.approvalRequired) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Please specify if approval is required",
      });
      return false;
    }

    // Enable Tax validation
    if (!data.enableTax) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Please specify if tax is enabled",
      });
      return false;
    }

    // Tax Application Level validation (conditional)
    if (data.enableTax === "true" && !data.taxApplicationLevel) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Tax Application Level is required when tax is enabled",
      });
      return false;
    }

    // Header Level Tax Type validation
    // if (
    //   data.enableTax === "true" &&
    //   data.taxApplicationLevel === TaxApplicationLevel.Header &&
    //   !data.headerTaxType
    // ) {
    //   Swal.fire({
    //     icon: "error",
    //     title: "Validation Error",
    //     text: "Tax Type is required for Header Level tax configuration",
    //   });
    //   return false;
    // }

    // Both Level TDS/TCS Tax Type validation - At least one is required
    if (
      data.enableTax === "true" &&
      data.taxApplicationLevel === TaxApplicationLevel.Both
    ) {
      if (!data.tdsTaxType && !data.tcsTaxType) {
        Swal.fire({
          icon: "error",
          title: "Validation Error",
          text: "At least one of TDS or TCS Tax Type is required for Both Level tax configuration",
        });
        return false;
      }
    }

    return true;
  };

  const submitForm = async (data: WorkOrderFormData) => {
    console.log("General Details data:", data);
    if (!validateForm(data)) {
      console.log("Validation failed");
      return;
    }

    // Set approvals data in form
    setValue("approvalsData", JSON.stringify(approvals));
    setValue("isStrictApproval", isStrictApproval);
    setValue("numberingMode", numberingMode);

    // Only block navigation if approval is required ("true") but no approvals set
    if (data.approvalRequired === "true" && approvals?.data?.length === 0) {
      Swal.fire({
        icon: "error",
        title: "Approval Required",
        text: "Please set the required approvals before proceeding to the next step.",
      });
      return;
    }

    const payload = {
      ...data,
      isStrictApproval: Boolean(isStrictApproval),
      approvalsData: JSON.stringify(approvals),
      step: "generalDetails",
    };

    console.log("General Details Payload:", payload);
    props.next(payload);
  };

  const [sequenceAccess, setSequenceAccess] = useState<any>(null);
  const [activeSequences, setActiveSequences] = useState<any[]>([]);
  const [numberingMode, setNumberingMode] = useState<'auto' | 'manual'>('auto');
  const [showNumberingModal, setShowNumberingModal] = useState(false);
  const [tempNumberingMode, setTempNumberingMode] = useState<'auto' | 'manual'>('auto');

  const fetchSequenceAccess = async () => {
    try {
      const response = await sequenceApis.fetchSequence();
      const { data } = response;
      const today = new Date();
      const _sequenceAccess = data?.filter(
        (item: any) =>
          item.moduleName === "Work Order" &&
          new Date(item.endDate) > today &&
          item.status === "Active"
      );
      setActiveSequences(_sequenceAccess || []);

      // Check if mode is already persisted in form (e.g. user navigated back from next step)
      const savedMode = getValues("numberingMode");
      if (savedMode) {
        setNumberingMode(savedMode);
        return;
      }

      // Set initial numbering mode based on available sequences
      if (_sequenceAccess && _sequenceAccess.length > 0 && siteIdWatch) {
        // Site-level first
        const hasSiteSequence = _sequenceAccess.some(
          (seq: any) => seq.level === "Site" && seq.siteId?.toString() === siteIdWatch?.toString()
        );
        if (hasSiteSequence) {
          setNumberingMode('auto');
          return;
        }
        // Org-level fallback
        const hasOrgSequence = _sequenceAccess.some(
          (seq: any) => seq.level === "Org"
        );
        setNumberingMode(hasOrgSequence ? 'auto' : 'manual');
      } else {
        setNumberingMode('manual');
      }
    } catch (error: any) {
      console.log(error.response?.data?.message || "An error occurred");
      setActiveSequences([]);
      setNumberingMode('manual');
    }
  };

  const getCurrentSiteSequence = () => {
    if (!siteIdWatch || activeSequences.length === 0) return null;

    const siteSequence = activeSequences.find(
      (seq) =>
        seq.level === "Site" &&
        seq.siteId?.toString() === siteIdWatch?.toString()
    );

    if (siteSequence) return siteSequence;

    const orgSequence = activeSequences.find(
      (seq) => seq.level === "Org"
    );

    if (orgSequence) return orgSequence;

    return null;
  };

  const currentSiteSequence = getCurrentSiteSequence();
  const canAuto = !!siteIdWatch && !!currentSiteSequence;

  useEffect(() => {
    const existingValue = getValues("workOrderNumber");

    if (numberingMode === 'auto' && currentSiteSequence && !existingValue) {
      const { prefix, nextNumber } = currentSiteSequence;
      const poNumber = `${prefix}`;
      setValue('workOrderNumber', poNumber);
    }
  }, [numberingMode, currentSiteSequence]);

  const prevSiteRef = useRef();

  useEffect(() => {
    const prevSite = prevSiteRef.current;

    // Only run if site actually changed
    if (prevSite && prevSite !== siteIdWatch) {
      setValue("workOrderNumber", "");
      // Clear persisted mode so new site gets fresh evaluation
      setValue("numberingMode", "");
    }

    // Respect persisted mode on remount
    const savedMode = getValues("numberingMode");
    if (savedMode) {
      setNumberingMode(savedMode);
    } else if (!siteIdWatch) {
      setNumberingMode("manual");
    } else {
      const siteSequence = getCurrentSiteSequence();
      setNumberingMode(siteSequence ? "auto" : "manual");
    }

    prevSiteRef.current = siteIdWatch;
  }, [siteIdWatch]);

  // Re-fetch sequences when component mounts and when site changes
  useEffect(() => {
    if (siteIdWatch) {
      fetchSequenceAccess();
    }
  }, [siteIdWatch]);

  return (
    <>
      <form className="content clearfix" onSubmit={handleSubmit(submitForm)}>
        <div className="card">
          <div className="card-body">
            {/* <div className="card-title">
              <h5>📋 Section A: General Information</h5>
              <p className="text-muted">Enter the basic work order information as per FSD specifications</p>
            </div> */}

            <Row className="gy-4">
              {/* Work Order Number - Auto-generated (FSD Field 1) */}
              {/* <Col lg="6">
                <FormInput
                  name="workOrderNumber"
                  label="Work Order Number"
                  required={true}
                  placeholder="Auto-generated"
                  disabled={true}
                  tooltipText="System-generated unique identifier"
                />
              </Col> */}

              {/* Site Selection - Mandatory (FSD Field 7) */}

              {/* Linked Work Request - Optional (FSD Field 2) */}
              <Col lg="6">
                <FormInput
                  name="linkedWorkRequest"
                  label="Link Work Request"
                  placeholder="Select Work Request (Optional)"
                  // disabled={!siteIdWatch}
                  tooltipText="Shows open work requests from WR module"
                />
              </Col>

              {/* Linked Tender - Optional (FSD Field 3) */}
              <Col lg="6">
                <FormInput
                  name="linkedTender"
                  label="Link Tender"
                  placeholder="Select Tender (Optional)"
                  // disabled={!siteIdWatch}
                  tooltipText="Shows open tenders if applicable"
                />
              </Col>

              {/* Contract Date - Mandatory (FSD Field 4) */}
              <Col lg="6">
                <FormDatePicker
                  name="contractDate"
                  label="Contract Date"
                  required={true}
                  maxDate={new Date()}
                  placeholder="Select Contract Date"
                // tooltipText="Date of agreement finalization"
                />
              </Col>

              {/* Work Order Title - Mandatory (FSD Field 5) */}
              <Col lg="6">
                <FormInput
                  name="workOrderTitle"
                  label="Work Order Title"
                  required={true}
                  placeholder="Enter work order title"
                  maxLength={200}
                  tooltipText="Title of WO"
                />
              </Col>

              {/* Work Order Description - Optional (FSD Field 6) */}
              <Col lg="12">
                <FormTextArea
                  name="workOrderDescription"
                  label="Work Order Description"
                  placeholder="Enter description of the scope of the WO (Optional)"
                  maxLength={500}
                  rows={3}
                // tooltipText="Description of the scope of the WO"
                />
              </Col>

              <Col lg="6">
                <FormSelect
                  name="siteId"
                  label="Site Selection"
                  options={sitesOptions}
                  required={true}
                  placeholder="Select Site"
                  tooltipText="Comes from Site Master"
                />
              </Col>

              {/* {(sequenceAccess?.level == "Site" &&
                !sequenceAccess?.siteIds?.includes(siteIdWatch)) ||
                sequenceAccess?.module == "" ? (
                <Col lg="6">
                  <FormInput
                    name="workOrderNumber"
                    label={`WO Number`}
                    required={true}
                    disabled={AllFormDetails?.type === "edit" ? true : false}
                    placeholder="Enter WO Number"
                  />
                </Col>
              ) : (
                <></>
              )} */}

              <Col lg="6">
                <div style={{ position: 'relative' }}>
                  <FormInput
                    name="workOrderNumber"
                    label="WO Number"
                    required={numberingMode === 'manual'}
                    disabled={numberingMode === 'auto' || AllFormDetails?.type === "draft"}
                    placeholder={numberingMode === 'auto' ? 'Auto-generated' : 'Enter WO Number'}
                  />
                  <Tooltip title="Click here to enable or disable auto-generation of WO numbers.">
                    <SettingOutlined
                      style={{
                        position: 'absolute',
                        right: '10px',
                        top: '38px',
                        transform: 'translateY(-50%)',
                        cursor: 'pointer',
                        fontSize: '18px',
                        color: '#1890ff',
                        zIndex: 1,
                        paddingTop: 15,
                      }}
                      onClick={() => {
                        setTempNumberingMode(numberingMode);
                        setShowNumberingModal(true);
                      }}
                    />
                  </Tooltip>
                </div>
              </Col>


              {/* Project Selection moved to Work Order Details level for Project Management source */}

              {/* Contract Type - Mandatory (FSD Field 9) */}
              <Col lg="6">
                <FormSelect
                  name="contractType"
                  label="Contract Type"
                  options={contractTypeOptions}
                  required={true}
                  placeholder="Select Contract Type"
                  tooltipText="Construction/Service/Maintenance/Others"
                />
              </Col>

              {/* Contract With - Mandatory (FSD Field 10) */}
              <Col lg="6">
                <FormSelect
                  name="contractWith"
                  label="Contract With"
                  options={contractWithOptions}
                  required={true}
                  placeholder="Select Contract With"
                  tooltipText="GC/Sub-contractor/Others"
                />
              </Col>

              {/* Contractor Selection - Conditional (FSD Field 11) */}
              <Col lg="6">
                <FormSelect
                  name="contractorId"
                  label={
                    isClientFlow ? "Client Selection" : "Contractor Selection"
                  }
                  options={contractorOptions}
                  required={true}
                  placeholder={
                    isClientFlow ? "Select Client" : "Select Contractor"
                  }
                  disabled={!siteIdWatch || siteIdWatch === ""}
                  tooltipText={
                    isClientFlow
                      ? "Populated from Client Master"
                      : "Populated from Contractor Master"
                  }
                  onChange={handleContractorChange}
                />
              </Col>

              {/* Work Order Owner - Mandatory (FSD Field 12) */}
              <Col lg="6">
                <FormSelect
                  name="workOrderOwner"
                  label="Work Order Owner"
                  options={userOptions}
                  required={true}
                  placeholder="Select Work Order Owner"
                  disabled={!siteIdWatch || siteIdWatch === ""}
                  tooltipText="Who created or owns the WO internally"
                  onChange={(userId: string) => {
                    if (userId) {
                      const selectedUser = userOptions.find(
                        (user) => user.value === userId
                      );
                      if (selectedUser) {
                        setValue("workOrderOwnerName", selectedUser.label);
                      }
                    } else {
                      setValue("workOrderOwnerName", "");
                    }
                  }}
                />
              </Col>

              {/* Planned Start Date - Mandatory (FSD Field 13) */}
              <Col lg="6">
                <FormDatePicker
                  name="plannedStartDate"
                  label="Planned Start Date"
                  required={true}
                  placeholder="Select Planned Start Date"
                  minDate={
                    contractDateWatch ? new Date(contractDateWatch) : undefined
                  }
                // tooltipText="Start date of execution"
                />
              </Col>

              {/* Planned End Date - Mandatory (FSD Field 14) */}
              <Col lg="6">
                <FormDatePicker
                  name="plannedEndDate"
                  label="Planned End Date"
                  required={true}
                  placeholder="Select Planned End Date"
                  minDate={
                    plannedStartDateWatch
                      ? new Date(plannedStartDateWatch)
                      : contractDateWatch
                        ? new Date(contractDateWatch)
                        : undefined
                  }
                // tooltipText="Completion date"
                />
              </Col>

              {/* Approval Required - Mandatory (FSD Field 15) */}
              <Col lg="6">
                <FormSelect
                  name="approvalRequired"
                  label="Approval Required ?"
                  options={[
                    { label: "Yes", value: "true" },
                    { label: "No", value: "false" },
                  ]}
                  placeholder="Select"
                  disabled={!siteIdWatch}
                  required={true}
                  tooltipText="If Yes, triggers approval workflow"
                />

                {/* Hidden field for approvals data */}
                <div style={{ display: "none" }}>
                  <FormInput name="approvalsData" label="" type="text" />
                </div>

                {/* Set Approvals Link */}
                {approvalRequiredWatch === "true" && (
                  <Popover
                    content="Click here to set required approvals"
                    title="Approval Required"
                  >
                    <a
                      href="#"
                      className="success"
                      onClick={(e) => {
                        e.preventDefault();
                        if (!siteIdWatch) {
                          message.error("Please select a site first");
                          return;
                        }
                        setApprovals((pre) => {
                          return {
                            ...pre,
                            open: true,
                          };
                        });
                      }}
                    >
                      <EditOutlined /> Set Approvals &nbsp;
                      {approvals?.data?.length === 0 ? (
                        <span className="mandatory">Please set approvals</span>
                      ) : (
                        <span className="text-success">
                          ✓ {approvals.data.length} approval(s) configured
                        </span>
                      )}
                    </a>
                  </Popover>
                )}
              </Col>

              {/* Enable Tax - Mandatory (FSD Field 16) */}
              <Col lg="6">
                <FormSelect
                  name="enableTax"
                  label="Enable Tax"
                  options={[
                    { label: "Yes", value: "true" },
                    { label: "No", value: "false" },
                  ]}
                  placeholder="Select"
                  required={true}
                  tooltipText="Enables tax logic"
                />
              </Col>

              {/* Tax Application Level - Conditional (FSD Field 17) */}
              {enableTaxWatch === "true" && (
                <Col lg="6">
                  <FormSelect
                    name="taxApplicationLevel"
                    label="Tax Level"
                    options={taxApplicationLevelOptions}
                    required={true}
                    placeholder="Select Tax Application Level"
                    tooltipText="Required if Enable Tax is Yes"
                  />
                </Col>
              )}

              {/* Header Level Tax Configuration */}
              {/* {enableTaxWatch === "true" && taxApplicationLevelWatch === TaxApplicationLevel.Header && (
                <Col lg="6">
                  <FormSelect
                    name="headerTaxType"
                    label="Tax Type"
                    options={taxOptions}
                    required={true}
                    placeholder="Select Tax Type"
                    tooltipText="Select tax type for work order level"
                  />
                </Col>
              )} */}

              {/* Both Level Tax Configuration - TDS */}
              {enableTaxWatch === "true" &&
                (taxApplicationLevelWatch === TaxApplicationLevel.Both ||
                  taxApplicationLevelWatch === TaxApplicationLevel.Header) && (
                  <>
                    <Col lg="6">
                      <FormSelect
                        name="tdsTaxType"
                        label="TDS"
                        options={tdsTaxOptions}
                        required={false}
                        placeholder="Select TDS Tax Type"
                        tooltipText="Select TDS tax type"
                      />
                    </Col>
                    <Col lg="6">
                      <FormSelect
                        name="tcsTaxType"
                        label="TCS"
                        options={tcsTaxOptions}
                        required={false}
                        placeholder="Select TCS Tax Type"
                        tooltipText="Select TCS tax type"
                      />
                    </Col>
                  </>
                )}
            </Row>
          </div>
        </div>

        <div className="actions clearfix">
          <ul>
            <li>
              <Button color="primary" type="submit">
                Next
              </Button>
            </li>
          </ul>
        </div>
      </form>

      {/* Work Order Approval Drawer */}
      {approvals.open && (
        <WorkOrderApprovalDrawer
          open={approvals.open}
          siteId={parseInt(siteIdWatch) || 0}
          onClose={() => {
            setApprovals((pre) => {
              return {
                ...pre,
                open: false,
              };
            });
          }}
          onSubmit={(data: any, isStrictApprovalFromDrawer: boolean) => {
            setApprovals({
              open: false,
              data: data,
            });
            setIsStrictApproval(isStrictApprovalFromDrawer);
          }}
          initialStrictApproval={isStrictApproval}
          setApprovalSubmissionStatus={(status: boolean) => { }}
          approvalData={approvals.data}
          moduleName="Work Order"
        />
      )}

      {/* wo sequence pop up */}
      <Modal
        title="Configure WO Number Preference"
        open={showNumberingModal}
        onCancel={() => setShowNumberingModal(false)}
        footer={
          <div style={{ display: "flex", justifyContent: "flex-start", gap: "10px" }}>
            <Button
              key="save"
              color="primary"
              onClick={() => {
                if (tempNumberingMode === "auto" && !currentSiteSequence) {
                  message.error("Auto-generation is not configured for this site.");
                  return;
                }

                setNumberingMode(tempNumberingMode);

                if (tempNumberingMode === "auto" && currentSiteSequence) {
                  const { prefix, nextNumber } = currentSiteSequence;
                  const woNumber = `${prefix}`;
                  setValue("workOrderNumber", woNumber);
                }
                else if (tempNumberingMode === "manual") {
                  setValue("workOrderNumber", "");
                }
                setShowNumberingModal(false);
              }}
            >
              Save
            </Button>

            <Button
              key="cancel"
              color="primary"
              onClick={() => setShowNumberingModal(false)}
            >
              Cancel
            </Button>
          </div>
        }
      >
        <div style={{ marginBottom: 20, lineHeight: "22px" }}>
          Your work order numbers are currently set to auto-generate to save time.
          <br />
          Are you sure you want to change this setting?
        </div>

        {!siteIdWatch && (
          <div style={{ color: "#8c8c8c", marginBottom: 16 }}>
            <InfoCircleOutlined /> Please select a site to check sequence configuration.
          </div>
        )}

        {siteIdWatch && !currentSiteSequence && (
          <div style={{ color: "#faad14", marginBottom: 16 }}>
            <InfoCircleOutlined />{" "}
            {activeSequences.length === 0
              ? "No sequences configured for Work Orders. Please set up a sequence first."
              : "No sequence configured for the selected site. Please set a sequence for this site."}
          </div>
        )}

        <Radio.Group
          onChange={(e) => setTempNumberingMode(e.target.value)}
          value={tempNumberingMode}
        >
          <Space direction="vertical" style={{ width: "100%" }}>
            <Radio value="auto" disabled={!canAuto}>
              <span style={{ fontWeight: 500 }}>
                Continue auto-generating work order numbers
              </span>
            </Radio>

            <Radio value="manual">
              <span style={{ fontWeight: 500 }}>
                Enter work order numbers manually
              </span>
            </Radio>
          </Space>
        </Radio.Group>
      </Modal>
    </>
  );
};

export default WorkOrderGeneralDetails;